import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import { ArrowDown, ArrowRight, Ban, CheckCircle2, FileSignature, Flag, Hourglass, Lock, ShieldAlert, UserRound, XCircle } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Button, refusedStep, useSave } from "@ncp/ui";
import { findingOf, type StageChecklist } from "../review/api";
import { OverrideFindingDialog } from "../review/OverrideFindingDialog";
import { useConfirmStep, useStepLettersRefusal } from "./StepLetters";
import { useRefuseRequest } from "./RefuseRequest";

export type InterestHold = "declare" | "pending" | "recused";

/**
 * The file's one next step, in the same place on every stage. When the step is a criterion already open below, the gold button is the
 * criterion's own and the header only says what it is; when nothing is the signed-in officer's to do, the header says who or what the
 * stage is waiting for instead of offering a button.
 */
export function StagePrimaryAction({ checklist, inCard, held, onAct }: {
  checklist: StageChecklist | null;
  /** The step's own gold button is on screen below. */
  inCard: boolean;
  /** Nothing on the stage can be done before the officer's declaration of interest is made (its gold button is in the banner below) or decided. */
  held?: InterestHold | null;
  onAct: (kind: string, target?: string | null) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { id: caseId } = useParams<{ id: string }>();
  const api = useApi();
  const qc = useQueryClient();
  const [overriding, setOverriding] = useState(false);
  // The end of a period is confirmed by the officer: completing its task takes the step in their name (Condition 3.2).
  const refused = useStepLettersRefusal();
  const confirm = useSave({
    mutationFn: ({ taskId, lettersFingerprint }: { taskId: string; lettersFingerprint: string | null }) => api.post(`/api/cases/${caseId}/workflow/tasks/${taskId}/complete`, { lettersFingerprint }),
    // The task is stored either way; when the step it takes was refused, the confirmation says only that, and the refused step is reported
    // beside it with its reason (S 2.8).
    saved: (r) => (refusedStep(r) ? t("backoffice:workspace.taskCompleted") : t("backoffice:r3Process.primary.confirmed")),
    onSuccess: () => { for (const k of [["case", caseId], ["case-workflow", caseId], ["case-review", caseId], ["case-tasks", caseId], ["case-events", caseId], ["case-review-history", caseId]]) void qc.invalidateQueries({ queryKey: k }); },
    onError: (e) => { refused(e); },
  });
  // A notice the step sends – the implicit rejection a lapse confirms – is shown in full before the confirmation (release 1.6).
  const step = useConfirmStep(caseId);
  // A request the Department decides on – the complainant's withdrawal – is refused with reasons where it is confirmed (Condition 3.2; Article 22(1)).
  const refuse = useRefuseRequest(caseId);
  if (held) {
    const Icon = held === "declare" ? ArrowDown : held === "pending" ? Hourglass : Ban;
    return (
      <p className="flex max-w-sm items-start gap-2 rounded-lg border border-sand-200 bg-sand-50 px-3 py-2 text-sm text-sand-900" data-next-step={held === "declare" ? "declare" : held === "pending" ? "awaiting" : "recused"}>
        <Icon className="mt-0.5 size-4 shrink-0 text-sand-700" /> <span>{t(`backoffice:stage.primary.held.${held}`)}</span>
      </p>
    );
  }
  if (!checklist) return null;
  const p = checklist.primary;
  const title = (locale === "ar" ? p.titleAr : p.title) ?? p.title ?? "";
  const gold = "h-auto min-h-10 max-w-sm whitespace-normal py-2 text-start leading-snug";

  switch (p.kind) {
    case "criterion":
      return inCard ? (
        <Button variant="ghost" className="h-auto min-h-10 max-w-sm whitespace-normal py-2 text-start leading-snug text-aegold-900" data-next-step="criterion" onClick={() => onAct("criterion", p.target)}>
          <ArrowDown /> <span>{t("backoffice:stage.primary.here", { title })}</span>
        </Button>
      ) : (
        <Button variant="gold" primary className={gold} data-next-step="criterion" onClick={() => onAct("criterion", p.target)}>
          <span>{t("backoffice:stage.primary.go", { title })}</span> <ArrowRight className="rtl:rotate-180" />
        </Button>
      );
    case "surface":
      return inCard ? (
        <Button variant="ghost" className="h-auto min-h-10 max-w-sm whitespace-normal py-2 text-start leading-snug text-aegold-900" data-next-step="surface" onClick={() => onAct("surface", p.surface)}>
          <ArrowDown /> <span>{t("backoffice:stage.primary.here", { title })}</span>
        </Button>
      ) : (
        <Button variant="gold" primary className={gold} data-next-step="surface" onClick={() => onAct("surface", p.surface)}>
          <span>{t("backoffice:stage.primary.go", { title })}</span> <ArrowRight className="rtl:rotate-180" />
        </Button>
      );
    case "issue":
      return <Button variant="gold" primary className={gold} data-next-step="issue" onClick={() => onAct("issue", p.target)}><Flag /> <span>{t("backoffice:stage.primary.issue", { title })}</span></Button>;
    case "finding": {
      const f = p.target ? findingOf(p.target) : null;
      const item = checklist.outstanding.find((i) => i.target === p.target);
      return (
        <>
          <Button variant="gold" primary className={gold} data-next-step="finding" onClick={() => setOverriding(true)}><ShieldAlert /> <span>{t("backoffice:r3Process.primary.finding", { title })}</span></Button>
          {caseId && f ? <OverrideFindingDialog caseId={caseId} finding={{ ...f, title: p.title ?? "", titleAr: p.titleAr, detail: item?.detail, detailAr: item?.detailAr }} mayOverride={!!checklist.mayOverride} open={overriding} onOpenChange={setOverriding} /> : null}
        </>
      );
    }
    case "confirm": {
      const taskId = p.target?.slice("task:".length);
      const request = checklist.outstanding.find((i) => i.kind === "Task" && (i.key === taskId || i.target === p.target));
      const confirmButton = (
        <Button variant="gold" primary className={gold} data-next-step="confirm" loading={confirm.isPending || step.checking} disabled={refuse.pending} onClick={() => taskId && void step.run({ taskId }, (lettersFingerprint) => confirm.mutate({ taskId, lettersFingerprint }))} title={title}>
          <CheckCircle2 /> <span>{t("backoffice:r3Process.primary.confirm", { title })}</span>
        </Button>
      );
      return (
        <>
          {request?.refusable ? (
            <span className="flex max-w-sm flex-wrap items-center gap-2" data-next-step-refusable>
              {confirmButton}
              <Button variant="outline" className="h-auto min-h-10" data-next-step="refuse" disabled={confirm.isPending || step.checking} loading={refuse.pending} onClick={() => refuse.open(request)}>
                <XCircle /> <span>{t("backoffice:r3Process.left.refuse")}</span>
              </Button>
            </span>
          ) : confirmButton}
          {step.dialog}
          {refuse.dialog}
        </>
      );
    }
    case "conclude":
      return <Button variant="gold" primary className={gold} data-next-step="conclude" onClick={() => onAct("conclude")}><FileSignature /> <span>{t("backoffice:stage.primary.conclude")}</span></Button>;
    case "waiting":
      return (
        <p className="flex max-w-sm items-start gap-2 rounded-lg border border-sand-200 bg-sand-50 px-3 py-2 text-sm text-sand-900" data-next-step="waiting">
          <Hourglass className="mt-0.5 size-4 shrink-0 text-sand-700" />
          <span>{title ? t("backoffice:stage.primary.waitingFor", { condition: title }) : t("backoffice:stage.primary.waiting")}</span>
        </p>
      );
    case "awaiting":
      return (
        <p className="flex max-w-sm items-start gap-2 rounded-lg border border-sand-200 bg-sand-50 px-3 py-2 text-sm text-sand-900" data-next-step="awaiting">
          <UserRound className="mt-0.5 size-4 shrink-0 text-sand-700" /> <span>{t("backoffice:stage.primary.awaiting", { role: checklist.ownerRole ? t(`roles.${checklist.ownerRole}`) : t("backoffice:stage.primary.anotherRole") })}</span>
        </p>
      );
    default:
      return (
        <p className="flex items-center gap-2 rounded-lg border border-sand-200 bg-sand-50 px-3 py-2 text-sm text-sand-900" data-next-step="none">
          <Lock className="size-4 shrink-0 text-sand-700" /> {t("backoffice:stage.primary.closed")}
        </p>
      );
  }
}
